/**
 * useChatDockWiring — the chat-dock ("chat docks to header") wiring extracted
 * from ChatBox (delegation pattern): the two dock callbacks
 * (measureChatColumnRect, handleDockChat), the derived isChatDockSplitActive,
 * and the dock effects (registerUndockRectProvider, landing-reveal timer,
 * setChatDockSplitViewActive, the expand-on-leave-split useLayoutEffect,
 * pendingOpenFocus focus). Preserves the view-transition / FLIP semantics.
 *
 * All hooks run at one fixed late position each render — order-stable, and
 * TDZ-safe since every dep comes from useChatDock (declared earlier) and no
 * effect has a same-render dependency on the intervening hooks.
 */
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { computeChatColumnRect, type DockRect } from '../components/chatbox/chat-dock-geometry';
import { CHAT_DOCK_LANDING_REVEAL_MS } from '@/common/motion/chatDockMotion';
import type { ChatDockState } from '@/app/providers/ChatDockContext';

export interface UseChatDockWiringDeps {
  isModulePaneFullscreenActive: boolean;
  isDesktopSplitViewport: boolean;
  chatDockState: ChatDockState;
  dockChat: (flightRect?: DockRect | null) => void;
  expandChatPane: () => void;
  setChatDockSplitViewActive: (active: boolean) => void;
  pendingOpenFocus: boolean;
  ackOpenFocus: () => void;
  pendingLandingReveal: boolean;
  ackLandingReveal: () => void;
  registerUndockRectProvider: (provider: (() => DockRect | null) | null) => void;
  chatBoxRootRef: RefObject<HTMLDivElement | null>;
  chatInputRef: RefObject<HTMLTextAreaElement | null>;
}

export interface UseChatDockWiringResult {
  measureChatColumnRect: () => DockRect | null;
  handleDockChat: () => void;
  isChatDockSplitActive: boolean;
}

export function useChatDockWiring(deps: UseChatDockWiringDeps): UseChatDockWiringResult {
  const {
    isModulePaneFullscreenActive,
    isDesktopSplitViewport,
    chatDockState,
    dockChat,
    expandChatPane,
    setChatDockSplitViewActive,
    pendingOpenFocus,
    ackOpenFocus,
    pendingLandingReveal,
    ackLandingReveal,
    registerUndockRectProvider,
    chatBoxRootRef,
    chatInputRef,
  } = deps;

  // Where the chat column sits (or will sit) — the flight surface's resting
  // rect. The root rect spans the whole split area, so it's stable across
  // every dock phase, including while the grid columns are still animating.
  // When the Daily Brief is docked in the column's top third (.chat-brief-pane
  // present), the chat occupies only the BOTTOM 2/3 — the flight must lift off
  // from / land on that rect, not the full column, or the glass covers the
  // Brief. The two docks are otherwise independent.
  const measureChatColumnRect = useCallback((): DockRect | null => {
    const rootEl = chatBoxRootRef.current;
    if (!rootEl || typeof window === 'undefined') return null;
    const { top, left, width, height } = rootEl.getBoundingClientRect();
    const rect = computeChatColumnRect({ top, left, width, height }, window.innerWidth);
    if (rootEl.querySelector('.chat-brief-pane')) {
      // Top third = the Brief's row (grid-template-rows 1fr 2fr, ChatBox.css).
      const briefRow = rect.height / 3;
      return { ...rect, top: rect.top + briefRow, height: rect.height - briefRow };
    }
    return rect;
  }, []);
  // Measure the lift-off rect before flipping state so the flight surface
  // mounts in the same commit the panel starts fading.
  const handleDockChat = useCallback(() => {
    dockChat(measureChatColumnRect());
  }, [dockChat, measureChatColumnRect]);
  // undockChat() measures through this provider before flipping state, so
  // the flight mounts in the same commit the launcher unmounts (the FLIP
  // needs the launcher's layout snapshot alive to morph from).
  useEffect(() => {
    registerUndockRectProvider(measureChatColumnRect);
    return () => registerUndockRectProvider(null);
  }, [registerUndockRectProvider, measureChatColumnRect]);
  // Close the post-undock landing window once the fade reveal has played.
  // While the flag is up the root wears .chat-dock-landing (set in the same
  // commit as the 'open' flip, so the slide-in reveal never flashes first).
  useEffect(() => {
    if (chatDockState !== 'open' || !pendingLandingReveal) return;
    const t = window.setTimeout(ackLandingReveal, CHAT_DOCK_LANDING_REVEAL_MS);
    return () => window.clearTimeout(t);
  }, [chatDockState, pendingLandingReveal, ackLandingReveal]);

  // The dock interaction only exists where the X exists: the desktop split
  // view at ≥768px (mobile renders the single-view chat | module toggle, no X).
  // Published into ChatDockContext so MainLayout knows when to render the
  // header launcher.
  // Layout effect, deliberately (mirrors the expand-on-leave-split effect
  // below): when a module open/close is wrapped in a view transition, the
  // visibility flip is flushed synchronously inside startViewTransition's
  // update callback — publishing the split state here (not post-paint) lets
  // MainLayout's Daily Brief placement swap (split cell ↔ solo top-third,
  // keyed on isSplitViewActive) commit inside that same flush, so the Brief's
  // view-transition-name pairs old/new and MORPHS with the chat instead of
  // snapping one frame after the transition captured its snapshots.
  const isChatDockSplitActive = isModulePaneFullscreenActive && isDesktopSplitViewport;
  useLayoutEffect(() => {
    setChatDockSplitViewActive(isChatDockSplitActive);
    return () => setChatDockSplitViewActive(false);
  }, [isChatDockSplitActive, setChatDockSplitViewActive]);

  // Leaving the split view while the chat is docked (module X'd, viewport
  // shrunk, conversation switched) puts the chat panel back on screen as the
  // main window — "docked" would be a lie, and the next module open would
  // wrongly restore fullscreen-with-launcher instead of the split. Reset to
  // open. Prev-value guard rather than effect cleanup: StrictMode re-runs
  // effects with the value unchanged, and a mount-time run must not clobber
  // the restored-docked load path.
  const prevChatDockSplitActiveRef = useRef(isChatDockSplitActive);
  // Layout effect, deliberately: when a docked-module exit is wrapped in a
  // view transition, the visibility flip is flushed synchronously inside
  // startViewTransition's update callback — running this here (not post-
  // paint) lets the expandChatPane commit land inside that same flush, so
  // the transition captures the true final state (chat back on screen).
  useLayoutEffect(() => {
    const prev = prevChatDockSplitActiveRef.current;
    prevChatDockSplitActiveRef.current = isChatDockSplitActive;
    if (prev && !isChatDockSplitActive && chatDockState !== 'open') {
      expandChatPane();
    }
  }, [isChatDockSplitActive, chatDockState, expandChatPane]);

  // After a user-initiated undock lands, return focus to the chat input.
  useEffect(() => {
    if (chatDockState === 'open' && pendingOpenFocus) {
      ackOpenFocus();
      chatInputRef.current?.focus();
    }
  }, [chatDockState, pendingOpenFocus, ackOpenFocus]);

  return {
    measureChatColumnRect,
    handleDockChat,
    isChatDockSplitActive,
  };
}
